// src/pages/tenant/TenantUsersPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, InputAdornment,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  Alert, Skeleton, Divider, useTheme, useMediaQuery, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Search as SearchIcon, PersonAdd as PersonAddIcon,
  Edit as EditIcon, Block as BlockIcon, CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon, Close as CloseIcon, Email as EmailIcon,
  Person as PersonIcon, Shield as ShieldIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────
type TenantRole = 'tenant_admin' | 'tenant_support' | 'tenant_billing';

interface StaffUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: TenantRole;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  inviteAcceptedAt?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const ROLE_CONFIG: Record<TenantRole, { label: string; color: string; bg: string; description: string }> = {
  tenant_admin: {
    label: 'Admin',
    color: '#f87171',
    bg: 'rgba(248,113,113,0.12)',
    description: 'Full access — manage companies, billing, invoices, staff',
  },
  tenant_support: {
    label: 'Support',
    color: '#38bdf8',
    bg: 'rgba(56,189,248,0.12)',
    description: 'View all data, impersonate companies to help clients',
  },
  tenant_billing: {
    label: 'Billing',
    color: '#34d399',
    bg: 'rgba(52,211,153,0.12)',
    description: 'Billing and invoices only — no company management',
  },
};

const ROLES: TenantRole[] = ['tenant_admin', 'tenant_support', 'tenant_billing'];

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

// ─── Invite Dialog ────────────────────────────────────────────────────────────
interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}

const InviteDialog: React.FC<InviteDialogProps> = ({ open, onClose, onInvited }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', role: 'tenant_support' as TenantRole });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#34d399' },
      '&.Mui-focused fieldset': { borderColor: '#34d399' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34d399' },
  };

  const reset = () => {
    setForm({ firstName: '', lastName: '', email: '', role: 'tenant_support' });
    setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleInvite = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('All fields are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/tenant-admin/staff/invite', form);
      onInvited();
      handleClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
              <PersonAddIcon sx={{ color: '#34d399', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: isDark ? '#fff' : '#0f172a' }}>
                Invite Team Member
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: subtext }}>
                They'll receive an email to set their password
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="First Name" value={form.firstName}
              onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
              size="small" sx={fieldSx} />
            <TextField fullWidth label="Last Name" value={form.lastName}
              onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
              size="small" sx={fieldSx} />
          </Stack>

          <TextField fullWidth label="Email Address" type="email" value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            size="small" sx={fieldSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: subtext }} /></InputAdornment> }}
          />

          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Role</InputLabel>
            <Select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value as TenantRole }))} label="Role">
              {ROLES.map(r => (
                <MenuItem key={r} value={r}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: ROLE_CONFIG[r].bg }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: ROLE_CONFIG[r].color }}>{ROLE_CONFIG[r].label}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: subtext }}>{ROLE_CONFIG[r].description}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Role description */}
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: ROLE_CONFIG[form.role].bg, border: `1px solid ${ROLE_CONFIG[form.role].color}33` }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ShieldIcon sx={{ fontSize: 16, color: ROLE_CONFIG[form.role].color }} />
              <Typography sx={{ fontSize: '0.78rem', color: ROLE_CONFIG[form.role].color, fontWeight: 700 }}>
                {ROLE_CONFIG[form.role].description}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleInvite} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
          {loading ? <CircularProgress size={18} /> : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Edit Role Dialog ─────────────────────────────────────────────────────────
interface EditRoleDialogProps {
  open: boolean;
  user: StaffUser | null;
  onClose: () => void;
  onUpdated: () => void;
}

const EditRoleDialog: React.FC<EditRoleDialogProps> = ({ open, user, onClose, onUpdated }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [role, setRole] = useState<TenantRole>('tenant_support');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  useEffect(() => { if (user) setRole(user.role); }, [user]);

  const handleUpdate = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      await api.patch(`/tenant-admin/staff/${user._id}`, { role });
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle>
        <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: isDark ? '#fff' : '#0f172a' }}>
          Change Role — {user?.firstName} {user?.lastName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Role</InputLabel>
          <Select value={role} onChange={e => setRole(e.target.value as TenantRole)} label="Role">
            {ROLES.map(r => (
              <MenuItem key={r} value={r}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: ROLE_CONFIG[r].bg }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: ROLE_CONFIG[r].color }}>{ROLE_CONFIG[r].label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.8rem', color: subtext }}>{ROLE_CONFIG[r].label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>Cancel</Button>
        <Button variant="contained" onClick={handleUpdate} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
          {loading ? <CircularProgress size={18} /> : 'Update Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TenantUsersPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState<StaffUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<StaffUser | null>(null);

  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tenant-admin/staff', {
        params: { search, role: roleFilter, page, limit },
      });
      setUsers(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const toggleActive = async (user: StaffUser) => {
    try {
      await api.patch(`/tenant-admin/staff/${user._id}`, { isActive: !user.isActive });
      setSuccess(`${user.firstName} ${user.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update user');
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <Box>
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)}
        onInvited={() => { setSuccess('Invitation sent successfully'); fetchUsers(); }} />
      <EditRoleDialog open={!!editUser} user={editUser} onClose={() => setEditUser(null)}
        onUpdated={() => { setSuccess('Role updated successfully'); fetchUsers(); }} />

      {/* ── Header ── */}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent="space-between" spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
            <PersonIcon sx={{ color: '#34d399', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>
              Team
            </Typography>
            <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>
              Manage platform staff and their access
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchUsers} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, fontSize: '0.78rem', background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
            Invite Member
          </Button>
        </Stack>
      </Stack>

      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      {/* ── Role summary chips ── */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        {ROLES.map(r => {
          const count = users.filter(u => u.role === r).length;
          return (
            <Box key={r} sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: ROLE_CONFIG[r].bg, border: `1px solid ${ROLE_CONFIG[r].color}33` }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: ROLE_CONFIG[r].color }}>
                {ROLE_CONFIG[r].label}: {count}
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext }}>
            Total: {total}
          </Typography>
        </Box>
      </Stack>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
          <TextField
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…" size="small"
            sx={{ minWidth: isMobile ? '100%' : 300 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: subtext }} /></InputAdornment> }}
          />
          <Stack direction="row" spacing={0.5}>
            {['all', ...ROLES].map(r => (
              <button key={r} onClick={() => setRoleFilter(r)}
                style={{
                  padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                  background: roleFilter === r
                    ? (r === 'all' ? '#34d399' : ROLE_CONFIG[r as TenantRole].color)
                    : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  color: roleFilter === r ? (r === 'all' ? '#000' : '#fff') : subtext,
                  transition: 'all 0.15s',
                }}>
                {r === 'all' ? 'All' : ROLE_CONFIG[r as TenantRole].label}
              </button>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={1.5}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={52} sx={{ borderRadius: 1 }} />)}
            </Stack>
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 40, color: subtext, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: headingColor }}>No team members found</Typography>
            <Typography variant="body2" sx={{ color: subtext, mt: 0.5 }}>
              Invite your first team member to get started
            </Typography>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)} sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000' }}>
              Invite Member
            </Button>
          </Box>
        ) : isMobile ? (
          // Mobile card list
          <Stack spacing={0}>
            {users.map((u, i) => (
              <Box key={u._id} sx={{ px: 2.5, py: 2, borderBottom: i < users.length - 1 ? `1px solid ${border}` : 'none' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: headingColor }}>
                      {u.firstName} {u.lastName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: subtext }}>{u.email}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                      <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: ROLE_CONFIG[u.role].bg }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: ROLE_CONFIG[u.role].color, textTransform: 'uppercase' }}>
                          {ROLE_CONFIG[u.role].label}
                        </Typography>
                      </Box>
                      <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: u.isActive ? 'rgba(52,211,153,0.12)' : 'rgba(100,116,139,0.1)' }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: u.isActive ? '#34d399' : '#64748b', textTransform: 'uppercase' }}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography sx={{ fontSize: '0.68rem', color: subtext, mt: 0.5 }}>
                      Last login: {fmtDate(u.lastLogin)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Change role">
                      <IconButton size="small" onClick={() => setEditUser(u)} sx={{ color: subtext }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton size="small" onClick={() => toggleActive(u)} sx={{ color: u.isActive ? '#f87171' : '#34d399' }}>
                        {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          // Desktop table
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                  {['Name', 'Email', 'Role', 'Status', 'Last Login', 'Joined', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, borderBottom: `1px solid ${border}`, py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u._id} hover sx={{ '& td': { borderBottom: `1px solid ${border}` } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: headingColor }}>
                        {u.firstName} {u.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.83rem', color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                        {u.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', px: 1.25, py: 0.35, borderRadius: 1.5, bgcolor: ROLE_CONFIG[u.role].bg }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: ROLE_CONFIG[u.role].color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                          {ROLE_CONFIG[u.role].label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 1.5, bgcolor: u.isActive ? 'rgba(52,211,153,0.12)' : 'rgba(100,116,139,0.1)' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: u.isActive ? '#34d399' : '#64748b' }} />
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: u.isActive ? '#34d399' : '#64748b', textTransform: 'uppercase' }}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(u.lastLogin)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(u.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Change role">
                          <IconButton size="small" onClick={() => setEditUser(u)} sx={{ color: subtext }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
                          <IconButton size="small" onClick={() => toggleActive(u)} sx={{ color: u.isActive ? '#f87171' : '#34d399' }}>
                            {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Pagination ── */}
        {users.length > 0 && (
          <>
            <Divider sx={{ borderColor: border }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" sx={{ color: subtext }}>
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Typography variant="caption" sx={{ color: subtext }}>Page {page} of {totalPages}</Typography>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </Stack>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default TenantUsersPage;